import type { ApiEnvelope } from '../types';
import { EnterHttpError } from '../http/errors';
import { isApiEnvelope } from '../http/envelope';
import type { ApiModuleDeps } from './internal/request';
import type {
    APIRequestLogEntry,
    ApiDomain,
    ApiFirstProjectCreditSupport,
    ApiFirstResultSeenRequest,
    ApiProject,
    ApiPreviewObservation,
    ApiProjectItemsQuery,
    ApiProjectItemsResponse,
    ApiProjectFileAssetBinding,
    ApiProjectFileDownload,
    ApiProjectFileListResponse,
    ApiProjectFileRecord,
    ApiProjectFileType,
    ApiProjectMember,
    ApiProjectSkillsResponse,
    ApiProjectSourceCode,
    ApiSkillDetail,
    ApiSourceCodeDiff,
    ApiTask,
    ConsoleLogEntry,
    CreateProjectSkillRef,
    PresenceMember,
    RemixTemplateRequest,
    SelectedAIAllModelRef,
    WorkspaceChatSummary,
    FailureEnvelopePayload,
    PublishErrorDetail,
} from '../types';
import {
    createPublicResourceResolver,
    shouldResolvePublicResource,
} from './resources/public-url-resolver';
import { cleanQuery, envelope } from './internal/request';

function createLivePreviewIdempotencyKey(projectId: string): string {
    const randomPart =
        typeof globalThis.crypto?.randomUUID === 'function'
            ? globalThis.crypto.randomUUID()
            : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    return `live-preview:${projectId}:${randomPart}`;
}

function validateLivePreviewResponse(response: unknown, headers?: Headers): void {
    if (typeof response === 'object' && response !== null && 'code' in response && response.code === 0) {
        return;
    }
    const envelope = isApiEnvelope(response) ? response : undefined;
    throw new EnterHttpError({
        message: envelope?.message || 'Live preview request failed',
        detail: envelope?.detail,
        responseBody: response,
        headers,
    });
}

export function createProjectsApi({ authHttp, noAuthHttp }: ApiModuleDeps) {
    const { getDownloadPublicResourcePresignedURLs } = createPublicResourceResolver(noAuthHttp);
    return {
        getCasePreviewAccess(projectId: string) {
            return envelope(
                authHttp.get<ApiEnvelope<{ allowed: boolean }>>(
                    `/projects/${projectId}/case-preview/access`,
                    { retry: 'never' }
                )
            );
        },
        getCasePreviewAccounts(projectId: string, page = 1) {
            return envelope(
                authHttp.get<
                    ApiEnvelope<{
                        accounts: { id: string; label: string }[];
                        next_page: number;
                    }>
                >(`/projects/${projectId}/case-preview/accounts`, {
                    query: { page },
                    retry: 'never',
                })
            );
        },
        issueCasePreview(
            projectId: string,
            request: { account_id: string; mode: 'live' | 'snapshot'; turn: number }
        ) {
            return envelope(
                authHttp.post<ApiEnvelope<{ token: string; expires_in: number }>>(
                    `/projects/${projectId}/case-preview/tickets`,
                    request,
                    { retry: 'never' }
                )
            );
        },
        getFirstProjectCreditSupport(projectId: string) {
            return envelope(
                authHttp.get<ApiEnvelope<ApiFirstProjectCreditSupport>>(
                    `/projects/${projectId}/first-project-credit-support`,
                    { retry: 'never' }
                )
            );
        },
        reportFirstResultSeen(projectId: string, request: ApiFirstResultSeenRequest) {
            return envelope(
                authHttp.post<ApiEnvelope<ApiFirstProjectCreditSupport>>(
                    `/projects/${projectId}/first-result-seen`,
                    request,
                    { retry: 'never' }
                )
            );
        },
        redeemCasePreview(projectId: string, token: string) {
            return envelope(
                authHttp.post<ApiEnvelope<{ url: string }>>(
                    `/projects/${projectId}/case-preview/redeem`,
                    { token },
                    { retry: 'never' }
                )
            );
        },

        async listProjectItems(
            workspacePublicId: string,
            query: ApiProjectItemsQuery = {}
        ): Promise<ApiProjectItemsResponse> {
            const response = await authHttp.get<ApiProjectItemsResponse>(
                `/workspaces/${encodeURIComponent(workspacePublicId)}/project-items`,
                { query: cleanQuery({ ...query }) }
            );

            const coverResources = Array.from(
                new Set(
                    response.items.map((item) => item.cover).filter(shouldResolvePublicResource)
                )
            );
            let resolvedCoverUrls: Record<string, string> = {};

            if (coverResources.length > 0) {
                try {
                    const urls = await getDownloadPublicResourcePresignedURLs(coverResources);
                    if (urls.code === 0) resolvedCoverUrls = urls.data?.urls ?? {};
                } catch (error) {
                    console.error('Failed to resolve Project Item covers:', error);
                }
            }

            return {
                ...response,
                items: response.items.map((item) => {
                    if (!item.cover || item.cover_url) return item;
                    const coverUrl = shouldResolvePublicResource(item.cover)
                        ? resolvedCoverUrls[item.cover]
                        : item.cover;
                    return coverUrl ? { ...item, cover_url: coverUrl } : item;
                }),
            };
        },

        async createProject(
            workspace_id: number,
            request: {
                name: string;
                prompt: string;
                plan_mode?: boolean;
                model_id?: string;
                /** 首次任务使用的已发布设计系统版本，由服务端绑定后再执行。 */
                design_system?: { design_system_id: string; revision_id: string };
                selected_ai_all_models?: SelectedAIAllModelRef[];
                images?: string[];
                attachments?: {
                    name: string;
                    size: number;
                    oss_key: string;
                }[];
                workspace_asset_ids?: string[];
                /** 引用的组件列表 */
                components?: {
                    id: string;
                }[];
                /** 项目创建后长期启用的 skills */
                enabled_skills?: CreateProjectSkillRef[];
                /** 本次创建强制注入的 skills */
                forced_skills?: CreateProjectSkillRef[];
            }
        ): Promise<ApiEnvelope<{ project: ApiProject; insufficient_credits?: boolean }>> {
            const response = await authHttp.post<
                ApiEnvelope<{ project: ApiProject; insufficient_credits?: boolean }>
            >(`/workspaces/${workspace_id}/projects`, request);
            return response;
        },

        async listProjectSkills(
            project_id: string
        ): Promise<ApiEnvelope<ApiProjectSkillsResponse>> {
            const response = await authHttp.get<ApiEnvelope<ApiProjectSkillsResponse>>(
                `/projects/${project_id}/skills`
            );
            return response;
        },

        async patchProjectSkills(
            project_id: string,
            request: {
                updates: Array<{
                    skill_key: string;
                    enabled: boolean;
                    version?: number;
                }>;
            }
        ): Promise<ApiEnvelope<{ success: boolean }>> {
            const response = await authHttp.patch<ApiEnvelope<{ success: boolean }>>(
                `/projects/${project_id}/skills`,
                request
            );
            return response;
        },

        async persistProjectSandboxSkill(
            project_id: string,
            request: {
                skill_path: string;
                source: 'sandbox';
            }
        ): Promise<ApiEnvelope<{ skill?: ApiSkillDetail }>> {
            const response = await authHttp.post<ApiEnvelope<{ skill?: ApiSkillDetail }>>(
                `/projects/${project_id}/skills`,
                request
            );
            return response;
        },

        async getProjectSourceCode(projectId: string): Promise<ApiEnvelope<ApiProjectSourceCode>> {
            const response = await authHttp.get<ApiEnvelope<ApiProjectSourceCode>>(
                `/projects/${projectId}/source-code`
            );
            return response;
        },

        async editProjectSourceCode(
            projectId: string,
            commit: string,
            sourceCodes: {
                code: string;
                file_path: string;
            }[],
            chatId?: string
        ) {
            const response = await authHttp.post(`/projects/${projectId}/edit-code`, {
                commit,
                source_codes: sourceCodes,
                ...(chatId ? { chat_id: chatId } : {}),
            });
            return response;
        },

        async listProjects(
            workspaceId: number,
            page?: number,
            page_size?: number,
            withCoverUrl?: boolean,
            options?: {
                search?: string;
                favorite?: boolean;
            }
        ): Promise<
            ApiEnvelope<{
                projects: ApiProject[];
                total?: number;
                page?: number;
                page_size?: number;
                workspace_chat_summary?: WorkspaceChatSummary;
            }>
        > {
            const params: Record<string, string | number | boolean> = {};
            if (page) params.page = page;
            if (page_size) params.page_size = page_size;
            if (options?.search) params.search = options.search;
            if (options?.favorite) params.favorite = 'true';
            const response = await authHttp.get<
                ApiEnvelope<{
                    projects: ApiProject[];
                    total?: number;
                    page?: number;
                    page_size?: number;
                    workspace_chat_summary?: WorkspaceChatSummary;
                }>
            >(`/workspaces/${workspaceId}/projects`, { query: params });
            const result = response;

            // 处理 cover 字段转换为 cover_url
            if (withCoverUrl && result.data?.projects) {
                const projects = result.data.projects;
                const projectsWithCover = projects.filter((project: ApiProject) => project.cover);
                if (projectsWithCover.length > 0) {
                    const coverResources = projectsWithCover.map(
                        (project: ApiProject) => project.cover!
                    );
                    const urlsResp = await getDownloadPublicResourcePresignedURLs(coverResources);
                    if (urlsResp.code === 0) {
                        const urlsMap = urlsResp.data.urls;
                        projects.forEach((project: ApiProject) => {
                            if (project.cover && urlsMap[project.cover]) {
                                project.cover_url = urlsMap[project.cover];
                            }
                        });
                    }
                }
            }

            return result;
        },

        async favoriteProject(projectId: string): Promise<ApiEnvelope<void>> {
            const response = await authHttp.post<ApiEnvelope<void>>(
                `/projects/${projectId}/favorite`
            );
            return response;
        },

        async unfavoriteProject(projectId: string): Promise<ApiEnvelope<void>> {
            const response = await authHttp.delete<ApiEnvelope<void>>(
                `/projects/${projectId}/favorite`
            );
            return response;
        },

        async getProject(project_id: string): Promise<ApiEnvelope<{ project: ApiProject }>> {
            const response = await authHttp.get<ApiEnvelope<{ project: ApiProject }>>(
                `/projects/${project_id}/detail`
            );
            return response;
        },

        async registerProjectFile(
            projectId: string,
            request: {
                display_name: string;
                oss_key: string;
            }
        ): Promise<ApiEnvelope<{ file: ApiProjectFileRecord }>> {
            const response = await authHttp.post<ApiEnvelope<{ file: ApiProjectFileRecord }>>(
                `/projects/${projectId}/files`,
                request
            );
            return response;
        },

        async listProjectFiles(
            projectId: string,
            query: {
                search?: string;
                file_type?: ApiProjectFileType;
                page?: number;
                page_size?: number;
            } = {}
        ): Promise<ApiEnvelope<ApiProjectFileListResponse>> {
            const response = await authHttp.get<ApiEnvelope<ApiProjectFileListResponse>>(
                `/projects/${projectId}/files`,
                { query: cleanQuery(query) }
            );
            return response;
        },

        async listProjectFileMentions(
            projectId: string,
            query: {
                search?: string;
                query?: string;
            } = {}
        ): Promise<ApiEnvelope<{ files: ApiProjectFileRecord[] }>> {
            const response = await authHttp.get<ApiEnvelope<{ files: ApiProjectFileRecord[] }>>(
                `/projects/${projectId}/files/mentions`,
                { query: cleanQuery(query) }
            );
            return response;
        },

        async getProjectFileDownloadURL(
            projectId: string,
            fileId: string
        ): Promise<ApiEnvelope<{ file: ApiProjectFileDownload }>> {
            const response = await authHttp.get<ApiEnvelope<{ file: ApiProjectFileDownload }>>(
                `/projects/${projectId}/files/${encodeURIComponent(fileId)}/download-url`
            );
            return response;
        },

        async getReferencedProjectFileDownloadURL(
            projectId: string,
            chatId: string,
            fileId: string
        ): Promise<ApiEnvelope<{ file: ApiProjectFileDownload }>> {
            const response = await authHttp.get<ApiEnvelope<{ file: ApiProjectFileDownload }>>(
                `/projects/${projectId}/chats/${encodeURIComponent(chatId)}/files/${encodeURIComponent(fileId)}/download-url`
            );
            return response;
        },

        async renameProjectFile(
            projectId: string,
            fileId: string,
            request: {
                display_name: string;
            }
        ): Promise<ApiEnvelope<{ file: ApiProjectFileRecord }>> {
            const response = await authHttp.patch<ApiEnvelope<{ file: ApiProjectFileRecord }>>(
                `/projects/${projectId}/files/${encodeURIComponent(fileId)}`,
                request
            );
            return response;
        },

        async deleteProjectFile(
            projectId: string,
            fileId: string
        ): Promise<ApiEnvelope<{ file: ApiProjectFileRecord }>> {
            const response = await authHttp.delete<ApiEnvelope<{ file: ApiProjectFileRecord }>>(
                `/projects/${projectId}/files/${encodeURIComponent(fileId)}`
            );
            return response;
        },

        /** 把已有 Enter OSS 项目文件登记为 Workspace 素材，不重新上传文件字节。 */
        async saveProjectFileAsAsset(
            projectId: string,
            fileId: string
        ): Promise<ApiEnvelope<{ asset: ApiProjectFileAssetBinding }>> {
            const response = await authHttp.post<
                ApiEnvelope<{ asset: ApiProjectFileAssetBinding }>
            >(`/projects/${projectId}/files/${encodeURIComponent(fileId)}/save-as-asset`);
            return response;
        },

        async renameProject(
            project_id: string,
            request: {
                name: string;
            }
        ): Promise<ApiEnvelope<{ project: ApiProject }>> {
            const response = await authHttp.post<ApiEnvelope<{ project: ApiProject }>>(
                `/projects/${project_id}/rename`,
                request
            );
            return response;
        },

        async deleteProject(
            project_id: string,
            uid?: number
        ): Promise<ApiEnvelope<{ project?: ApiProject; projects?: ApiProject[] }>> {
            const params = project_id === 'all' && uid ? { uid: uid.toString() } : {};
            const response = await authHttp.delete<
                ApiEnvelope<{ project?: ApiProject; projects?: ApiProject[] }>
            >(`/projects/${project_id}/delete`, { query: params });
            return response;
        },

        async updateProjectVisibility(
            project_id: string,
            visibility: 'public' | 'workspace-only' | 'private'
        ): Promise<ApiEnvelope<{ project: ApiProject }>> {
            const response = await authHttp.post<ApiEnvelope<{ project: ApiProject }>>(
                `/projects/${project_id}/update_visibility`,
                {
                    visibility,
                }
            );
            return response;
        },

        async updateProjectBadge(
            project_id: string,
            open_badge: boolean
        ): Promise<ApiEnvelope<null>> {
            const response = await authHttp.post<ApiEnvelope<null>>(
                `/projects/${project_id}/badge`,
                {
                    open_badge,
                }
            );
            return response;
        },

        async getProjectDomains(
            project_id: string,
            options?: { status?: 'ready' | 'not_connnected' }
        ): Promise<ApiEnvelope<{ domains: ApiDomain[] }>> {
            const params = options?.status ? { status: options.status } : undefined;
            const response = await authHttp.get<ApiEnvelope<{ domains: ApiDomain[] }>>(
                `/projects/${project_id}/domains`,
                { query: params }
            );
            return response;
        },

        async addProjectDomain(
            project_id: string,
            domain_type: string,
            domain: string
        ): Promise<ApiEnvelope<{ domain: ApiDomain }>> {
            const response = await authHttp.post<ApiEnvelope<{ domain: ApiDomain }>>(
                `/projects/${project_id}/domain`,
                {
                    domain_type,
                    domain,
                }
            );
            return response;
        },

        async refreshProjectDomain(
            project_id: string,
            domain: string
        ): Promise<ApiEnvelope<{ domain: ApiDomain }>> {
            const response = await authHttp.post<ApiEnvelope<{ domain: ApiDomain }>>(
                `/projects/${project_id}/domain/refresh`,
                {
                    domain,
                }
            );
            return response;
        },

        async deleteProjectDomain(project_id: string, domain: string): Promise<ApiEnvelope<null>> {
            const response = await authHttp.delete<ApiEnvelope<null>>(
                `/projects/${project_id}/domain`,
                {
                    body: {
                        domain,
                    },
                }
            );
            return response;
        },

        async updateProjectDomain(
            project_id: string,
            origin_domain: string,
            new_domain: string
        ): Promise<ApiEnvelope<{ domain: ApiDomain }>> {
            const response = await authHttp.post<ApiEnvelope<{ domain: ApiDomain }>>(
                `/projects/${project_id}/domain/update`,
                {
                    origin_domain,
                    new_domain,
                }
            );
            return response;
        },

        async startSandboxLivePreview(
            projectId: string,
            options?: { signal?: AbortSignal }
        ): Promise<
            ApiEnvelope<{ url: string; reason?: string; failure?: FailureEnvelopePayload }>
        > {
            // 一个启动周期复用幂等键与共享重试预算，只有耗尽后的最终失败交给页面。
            const idempotencyKey = createLivePreviewIdempotencyKey(projectId);
            const response = await authHttp.post<
                ApiEnvelope<{
                    url: string;
                    reason?: string;
                    failure?: FailureEnvelopePayload;
                }>
            >(`/projects/${projectId}/sandbox/live-preview`, undefined, {
                headers: { 'X-Idempotency-Key': idempotencyKey },
                retry: 'idempotent',
                retryOnError: true,
                validateResponse: validateLivePreviewResponse,
                signal: options?.signal,
            });
            return response;
        },

        async keepAliveSandboxDevServer(projectId: string, options?: { signal?: AbortSignal }) {
            const response = await authHttp.get(`/projects/${projectId}/sandbox/keep-alive`, {
                // keep-alive 是非关键后台心跳；失败后等待下个周期，不能继续给限流加压。
                retry: 'never',
                signal: options?.signal,
            });
            return response;
        },

        async deleteSandbox(projectId: string): Promise<{ message: string }> {
            const response = await authHttp.delete<{ message: string }>(
                `/projects/${projectId}/sandbox`
            );
            return response;
        },

        async checkRelease(projectId: string) {
            const response = await authHttp.get(`/projects/${projectId}/check-release`);
            return response;
        },

        async createRelease(projectId: string): Promise<
            ApiEnvelope<{
                project: ApiProject;
                /** 本次发布包含的改动次数 */
                change_count: number;
                publish_error?: PublishErrorDetail;
            }>
        > {
            const response = await authHttp.post<
                ApiEnvelope<{
                    project: ApiProject;
                    /** 本次发布包含的改动次数 */
                    change_count: number;
                    publish_error?: PublishErrorDetail;
                }>
            >(`/projects/${projectId}/publish`);
            return response;
        },

        async takeProjectOffline(projectId: string): Promise<
            ApiEnvelope<{
                project: ApiProject;
            }>
        > {
            const response = await authHttp.post<
                ApiEnvelope<{
                    project: ApiProject;
                }>
            >(`/projects/${projectId}/offline`);
            return response;
        },

        async restoreProjectOnline(projectId: string): Promise<
            ApiEnvelope<{
                project: ApiProject;
            }>
        > {
            const response = await authHttp.post<
                ApiEnvelope<{
                    project: ApiProject;
                }>
            >(`/projects/${projectId}/online`);
            return response;
        },

        async getReleases(projectId: string) {
            const response = await authHttp.get(`/projects/${projectId}/release`);
            return response;
        },

        async resetRelease(projectId: string, version: string) {
            const response = await authHttp.post(`/projects/${projectId}/release/reset`, {
                version,
            });
            return response;
        },

        async buildProject(
            project_id: string,
            commit_id: string
        ): Promise<ApiEnvelope<{ task: ApiTask }>> {
            const response = await authHttp.post<ApiEnvelope<{ task: ApiTask }>>(
                `/projects/${project_id}/build/${commit_id}`
            );
            return response;
        },

        async getProjectMembers(
            project_id: string
        ): Promise<ApiEnvelope<{ members: ApiProjectMember[] }>> {
            const response = await authHttp.get<ApiEnvelope<{ members: ApiProjectMember[] }>>(
                `/projects/${project_id}/members`
            );
            return response;
        },

        async addProjectMember(
            project_id: string,
            email: string,
            role: string = 'viewer'
        ): Promise<ApiEnvelope<{ member: ApiProjectMember }>> {
            const response = await authHttp.post<ApiEnvelope<{ member: ApiProjectMember }>>(
                `/projects/${project_id}/members`,
                {
                    email,
                    role,
                }
            );
            return response;
        },

        async getProjectPresenceMembers(
            project_id: string
        ): Promise<ApiEnvelope<{ members: PresenceMember[] }>> {
            const response = await authHttp.get<ApiEnvelope<{ members: PresenceMember[] }>>(
                `/projects/${project_id}/presence/members`
            );
            return response;
        },

        async sendProjectPresenceHeartbeat(project_id: string): Promise<ApiEnvelope<null>> {
            const response = await authHttp.post<ApiEnvelope<null>>(
                `/projects/${project_id}/presence/heartbeat`
            );
            return response;
        },

        async sendProjectPresenceLeave(project_id: string): Promise<ApiEnvelope<null>> {
            const response = await authHttp.post<ApiEnvelope<null>>(
                `/projects/${project_id}/presence/leave`
            );
            return response;
        },

        async connectStripe(
            project_id: string,
            stripe_secret_key: string,
            chatId?: string
        ): Promise<ApiEnvelope<{ project: ApiProject }>> {
            const response = await authHttp.post<ApiEnvelope<{ project: ApiProject }>>(
                `/projects/${project_id}/connect-stripe`,
                {
                    stripe_secret_key,
                    ...(chatId ? { chat_id: chatId } : {}),
                }
            );
            return response;
        },

        async updateStripeAndMigrate(
            project_id: string,
            new_secret_key: string,
            product_ids: string[]
        ): Promise<ApiEnvelope<{ project: ApiProject }>> {
            const response = await authHttp.post<ApiEnvelope<{ project: ApiProject }>>(
                `/projects/${project_id}/stripe/update-and-migrate`,
                {
                    new_secret_key,
                    product_ids,
                }
            );
            return response;
        },

        async remixProject(
            project_id: string,
            request?: RemixTemplateRequest
        ): Promise<ApiEnvelope<{ project: ApiProject }>> {
            const response = await authHttp.post<ApiEnvelope<{ project: ApiProject }>>(
                `/projects/${project_id}/remix`,
                request || {}
            );
            return response;
        },

        async updateProject(
            project_id: string,
            data: {
                name?: string;
                description?: string;
                visibility?: 'public' | 'workspace-only' | 'private';
            }
        ): Promise<ApiEnvelope<{ project: ApiProject }>> {
            const response = await authHttp.patch<ApiEnvelope<{ project: ApiProject }>>(
                `/projects/${project_id}`,
                data
            );
            return response;
        },

        getPreviewDiffSourceCode(
            projectId: string,
            turn: number
        ): Promise<ApiEnvelope<{ diff: ApiSourceCodeDiff[] }>> {
            return authHttp.get('/projects/' + projectId + '/thread/turn/' + turn + '/diff');
        },

        /** 沿用默认用户鉴权；观察失败由调用方处理，不重试或触发页面登录。 */
        reportPreviewObservation(
            projectId: string,
            observation: ApiPreviewObservation,
            signal: AbortSignal
        ): Promise<ApiEnvelope<Record<string, never>>> {
            return envelope(authHttp.post('/projects/' + projectId + '/report_preview_observation', observation, {
                signal,
                timeoutMs: 3000,
                retry: 'never',
                errorHandling: 'caller',
            }));
        },

        reportConsoleLogs(
            projectId: string,
            logs: ConsoleLogEntry[]
        ): Promise<ApiEnvelope<Record<string, never>>> {
            return authHttp.post('/projects/' + projectId + '/report_console_logs', logs);
        },

        reportApiLogs(
            projectId: string,
            logs: APIRequestLogEntry[]
        ): Promise<ApiEnvelope<Record<string, never>>> {
            return authHttp.post('/projects/' + projectId + '/report_api_logs', logs);
        },

        enablePlanMode(
            projectId: string,
            chatId: string
        ): Promise<ApiEnvelope<Record<string, never>>> {
            return authHttp.post('/projects/' + projectId + '/plan-mode/enable', undefined, {
                query: { chat_id: chatId },
            });
        },

        disablePlanMode(
            projectId: string,
            chatId: string
        ): Promise<ApiEnvelope<Record<string, never>>> {
            return authHttp.post('/projects/' + projectId + '/plan-mode/disable', undefined, {
                query: { chat_id: chatId },
            });
        },
    };
}
